import React, { useEffect, useState } from "react";
import { useAuth } from '../AuthContext';
import api from "../services/api";
import Select from 'react-select';
import ThemeCustomizer from "../components/ThemeCustomizer";
import { AvatarModal } from '../components/AvatarModal';
import PurchaseStateModal from "../components/PurchaseStateModal";
import TwoFactorSettings from "../components/TwoFactorSettings";
import { getDefaultAvatar } from '../utils/avatarUtils';
import '../css/ProfileSettings.css';


export default function ProfileSettings() {
  const { user, refreshUser } = useAuth();

  // ---- ROLES/PERMISSIONS (future extensibility) ----
  // const roles = user?.roles || (user?.role ? [user.role] : []);
  // const permissions = user?.permissions || [];

  // ---- State ----
  const [tab, setTab] = useState("account");
  const [profile, setProfile] = useState(user || {});
  const [states, setStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([1]);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [purchaseModalState, setPurchaseModalState] = useState(null);

  // --- Fetch user audit log when security tab opens ---
  useEffect(() => {
    if (tab === "security") {
      setLoadingLog(true);
      api.get("/users/me/audit-log")
        .then(res => setAuditLog(res.data || []))
        .catch(() => setAuditLog([]))
        .finally(() => setLoadingLog(false));
    }
  }, [tab]);

  // --- Load profile and entitlements on mount ---
  useEffect(() => {
    async function load() {
      try {
        const [profileRes, statesRes] = await Promise.all([
          api.get("/users/me"),
          api.get("/states/with-entitlements"),
        ]);
        setProfile(profileRes.data);
        setStates(statesRes.data || []);
        // Always include National Pool (1)
        const owned = (statesRes.data || []).filter(st => st.owned).map(st => st.state_id);
        if (!owned.includes(1)) owned.unshift(1);
        setSelectedStates(owned);
      } catch (e) {
        setMsg(e.response?.data?.message || "Could not load profile/settings.");
      }
    }
    load();
  }, []);

  // --- Group dropdown options ---
  const nationalOption = states.find(st => st.state_id === 1)
    ? [{
        value: 1,
        label: `National Pool (${states.find(st => st.state_id === 1)?.question_count || 0})`,
        owned: true,
        state_id: 1,
      }]
    : [];

  const ownedOptions = states
    .filter(st => st.owned && st.state_id !== 1)
    .map(st => ({
      value: st.state_id,
      label: `${st.state_name} (${st.question_count})`,
      owned: true,
      state_id: st.state_id,
    }));

  const unownedOptions = states
    .filter(st => !st.owned)
    .map(st => ({
      value: st.state_id,
      label: `${st.state_name} (${st.question_count})`,
      owned: false,
      state_id: st.state_id,
      price: st.price,
    }));

  const groupedOptions = [
    { label: 'Owned States', options: [...nationalOption, ...ownedOptions] },
    { label: 'Available to Purchase', options: unownedOptions },
  ];

  // --- Handle Select Change (never allow removal of National Pool) ---
  const handleStateSelectChange = (selected) => {
    let values = selected.map(opt => opt.value);
    if (!values.includes(1)) values.unshift(1); // Always include
    // Only allow owned (or National) in the selection
    const allowed = values.filter(id =>
      states.find(st => st.state_id === id)?.owned || id === 1
    );
    setSelectedStates(allowed);
  };

  // --- Custom Option Rendering ---
  const formatOptionLabel = (option) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color: option.owned ? undefined : '#bbb' }}>
        {option.label}
      </span>
      {option.owned && option.state_id === 1 && (
        <span style={{
          marginLeft: 6, fontSize: 11, color: "#3399ff", fontStyle: "italic"
        }}>Always included</span>
      )}
      {!option.owned && (
        <button
          type="button"
          className="purchase-btn"
          onClick={e => {
            e.stopPropagation();
            setPurchaseModalState(option);
          }}
        >
          Purchase
        </button>
      )}
    </div>
  );

  // --- Save Handler (future optimization: only changed fields) ---
  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.put("/users/me", {
        ...profile,
        state_entitlements: selectedStates,
      });
      setMsg("Profile updated.");
      refreshUser && refreshUser();
    } catch (e) {
      setMsg(e.response?.data?.message || "Could not save changes.");
    }
    setSaving(false);
  };

  // --- Avatar Modal Handler (kept before return for clarity) ---
  async function handleAvatarChange(newUrl) {
    setShowAvatarModal(false);
    setProfile((p) => ({ ...p, avatar_url: newUrl }));
    try {
      await api.put("/users/me", { avatar_url: newUrl });
      refreshUser && refreshUser();
      setMsg("Avatar updated!");
    } catch {
      setMsg("Could not update avatar.");
    }
  }

  // --- Render ---
  return (
    <div className="profile-container modern-profile">
      <h2>Profile Settings</h2>
      {/* (Optional for debug/future:) */}
      {/* <div style={{ fontSize:12, color:'#888', margin:'8px 0 10px' }}>
        Roles: {roles.join(', ')} | Permissions: {permissions.join(', ')}
      </div> */}
      <div className="profile-tabs" role="tablist">
        <button className={tab === "account" ? "active" : ""} onClick={() => setTab("account")}>Account</button>
        <button className={tab === "entitlements" ? "active" : ""} onClick={() => setTab("entitlements")}>States</button>
        <button className={tab === "theme" ? "active" : ""} onClick={() => setTab("theme")}>Theme</button>
        <button className={tab === "security" ? "active" : ""} onClick={() => setTab("security")}>Security</button>
      </div>

      {/* --- Account Tab --- */}
      {tab === "account" && (
        <>
          <div className="profile-header">
            <div className="profile-avatar-block">
              <div className="profile-avatar-wrapper">
                <img
                  src={profile.avatar_url || getDefaultAvatar()}
                  alt="User avatar"
                  className="profile-avatar-img"
                  style={{ 
                    width: 76, 
                    height: 76, 
                    borderRadius: "50%", 
                    objectFit: "cover",
                    maxWidth: "76px",
                    maxHeight: "76px"
                  }}
                />
                <button
                  className="profile-avatar-edit-btn"
                  type="button"
                  title="Change avatar"
                  onClick={() => setShowAvatarModal(true)}
                >
                  <span role="img" aria-label="edit">✏️</span>
                </button>
              </div>
              <div className="profile-avatar-social-btns">
                <button className="profile-avatar-social-btn" disabled title="Coming soon">
                  Import from...
                </button>
              </div>
            </div>
            <div className="profile-header-main">
              <h3>{profile.first_name} {profile.last_name}</h3>
              <div className="username">{profile.email}</div>
            </div>
          </div>
          <div className="profile-info-section">
            <label>
              First Name:
              <input
                type="text"
                value={profile.first_name || ""}
                onChange={e => setProfile((p) => ({ ...p, first_name: e.target.value }))}
              />
            </label>
            <label>
              Last Name:
              <input
                type="text"
                value={profile.last_name || ""}
                onChange={e => setProfile((p) => ({ ...p, last_name: e.target.value }))}
              />
            </label>
            <label>
              Email:
              <input
                type="email"
                value={profile.email || ""}
                readOnly
              />
            </label>
            <button className="action-button" type="button" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </>
      )}

      {/* --- State Entitlements Tab --- */}
      {tab === "entitlements" && (
        <div className="profile-states-section">
          <h3>Exam States</h3>
          <div style={{ margin: "18px 0 10px" }}>
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={groupedOptions}
              value={groupedOptions.flatMap(g => g.options).filter(opt => selectedStates.includes(opt.value))}
              isOptionDisabled={option => !option.owned}
              onChange={handleStateSelectChange}
              formatOptionLabel={formatOptionLabel}
              menuPlacement="auto"
              placeholder="Select exam states..."
              styles={{
                option: (provided, state) => ({
                  ...provided,
                  color: !state.data.owned ? "#bbb" : undefined,
                  backgroundColor: state.isSelected ? "#e0f2fe" : undefined,
                  opacity: !state.data.owned ? 0.7 : 1,
                  cursor: !state.data.owned ? "not-allowed" : "pointer"
                })
              }}
            />
            <small style={{ color: "#888" }}>National Pool is always included (cannot be removed).</small>
          </div>
          <button className="action-button" style={{ marginTop: 12 }} type="button" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save State Entitlements"}
          </button>
        </div>
      )}

      {/* --- Theme Tab --- */}
      {tab === "theme" && <ThemeCustomizer profile={profile} />}

      {/* --- Security Tab --- */}
      {tab === "security" && (
        <div style={{ padding: 22 }}>
          {/* Two-Factor Authentication Section */}
          <div style={{ marginBottom: 40 }}>
            <TwoFactorSettings />
          </div>

          {/* Account Activity Section */}
          <div>
            <h3>Account Activity</h3>
            {loadingLog ? (
              <div>Loading log...</div>
            ) : auditLog.length === 0 ? (
              <div>No recent account activity.</div>
            ) : (
              <table className="audit-log-table" style={{ width: "100%", fontSize: 14, marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Action</th>
                    <th>Details</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((item, idx) => {
                    let details = "";
                    if (item.action_type === "purchase") {
                      details = `Purchased ${item.action_detail?.state_name || ""} (${item.action_detail?.price === 0 ? "Free" : `$${item.action_detail?.price}`})`;
                    } else if (item.action_type === "login") {
                      details = `Login from ${item.action_detail?.ip || "unknown IP"}`;
                    } else if (item.action_type === "profile_update") {
                      details = `Updated fields: ${(item.action_detail?.changed_fields || []).join(", ")}`;
                    } else {
                      details = JSON.stringify(item.action_detail);
                    }
                    return (
                      <tr key={idx}>
                        <td style={{ textTransform: "capitalize" }}>{item.action_type}</td>
                        <td>{details}</td>
                        <td>{new Date(item.created_at).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* --- Avatar Modal --- */}
      {showAvatarModal && (
        <AvatarModal
          currentAvatar={profile.avatar_url}
          onClose={() => setShowAvatarModal(false)}
          onAvatarChange={handleAvatarChange}
        />
      )}

      {/* --- Purchase Modal --- */}
      {purchaseModalState && (
        <PurchaseStateModal
          state={purchaseModalState}
          onClose={() => setPurchaseModalState(null)}
          onPurchased={(newStateId) => {
            api.get("/states/with-entitlements").then(res => setStates(res.data));
            setPurchaseModalState(null);
          }}
        />
      )}

      {/* --- Toast/Feedback --- */}
      {msg && (
        <div className="toast toast-info" style={{ marginTop: 14 }}>
          {msg}
        </div>
      )}
    </div>
  );
}
